import React, { useId, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, Layers, ArrowRight } from "lucide-react";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { queryClient } from "@/ajax/queryClient";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  HiringProcessItemStatus,
  HiringProcessInstanceStatus,
} from "@api/shared-runtime/enums";
import { toast } from "sonner";
import { instanceStatusBadge, ProgressBar } from "./helpers";
import { HiringProcessInstanceItemRow } from "./item-row";

export { HiringProcessProgressBadge } from "./progress-badge";

interface HiringProcessInstanceProgressProps {
  jobPostId: string;
  providerUserId: string;
  /** If true, org items are editable */
  canEditOrgItems?: boolean;
}

export function HiringProcessInstanceProgress({
  jobPostId,
  providerUserId,
  canEditOrgItems = false,
}: HiringProcessInstanceProgressProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const detailsId = useId();
  const trpc = useTRPC();
  const { selectedOrgBusinessUnit } = useOrganization();
  const buId = selectedOrgBusinessUnit.id;

  const { data: instances } = useQuery(
    trpc.hiringProcess.listCandidates.queryOptions({
      organizationBusinessUnitId: buId,
      jobPostId,
    }),
  );

  const updateStatusMutation = useMutation(
    trpc.hiringProcess.updateItemStatus.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries(
          trpc.hiringProcess.listCandidates.queryOptions({
            organizationBusinessUnitId: buId,
            jobPostId,
          }),
        );
        toast.success("Item status updated");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to update status");
      },
    }),
  );

  const transitionStageMutation = useMutation(
    trpc.hiringProcess.transitionStage.mutationOptions({
      onSuccess: async () => {
        // The dashboard's candidate cards and sidebar counts read the stage
        // from getInterestedCandidates, so refresh it alongside the checklist.
        await Promise.all([
          queryClient.invalidateQueries(
            trpc.hiringProcess.listCandidates.queryOptions({
              organizationBusinessUnitId: buId,
              jobPostId,
            }),
          ),
          queryClient.invalidateQueries({
            queryKey: trpc.jobPost.getInterestedCandidates.queryKey(),
          }),
        ]);
        toast.success("Stage updated");
      },
      onError: (err) => {
        toast.error(err.message || "Failed to transition stage");
      },
    }),
  );

  // Fetch template details to get stage list (listInstances only has currentStage)
  const instance = instances?.find((i) => i.userId === providerUserId);
  const templateId = instance?.hiringProcessId;

  const { data: templateDetails } = useQuery(
    trpc.hiringProcess.get.queryOptions(
      { organizationBusinessUnitId: buId, id: templateId ?? "" },
      { enabled: isExpanded && !!templateId },
    ),
  );

  if (!instance) {
    return null;
  }

  const allItems = instance.items;
  const totalItems = allItems.length;
  const completedItems = allItems.filter(
    (item) =>
      item.status === HiringProcessItemStatus.Completed ||
      item.status === HiringProcessItemStatus.Skipped ||
      item.status === HiringProcessItemStatus.NotApplicable,
  ).length;
  const requiredItems = allItems.filter((item) => item.isRequired);
  const completedRequired = requiredItems.filter(
    (item) =>
      item.status === HiringProcessItemStatus.Completed ||
      item.status === HiringProcessItemStatus.Skipped ||
      item.status === HiringProcessItemStatus.NotApplicable,
  ).length;

  const currentStage = instance.currentStage;
  const currentStageId = currentStage?.id ?? null;
  const templateStages = templateDetails?.stages ?? [];

  const handleStatusChange = (
    hiringProcessItemId: string,
    status: HiringProcessItemStatus,
  ) => {
    updateStatusMutation.mutate({
      jobPostId,
      userId: providerUserId,
      hiringProcessItemId,
      status,
    });
  };

  const handleTransitionStage = (toStageId: string) => {
    transitionStageMutation.mutate({
      organizationBusinessUnitId: buId,
      jobPostId,
      userId: providerUserId,
      toStageId,
    });
  };

  return (
    <div className="mt-3 min-w-0 overflow-hidden rounded-xl border border-slate-200">
      <button
        type="button"
        aria-expanded={isExpanded}
        aria-controls={detailsId}
        onClick={() => setIsExpanded((prev) => !prev)}
        className="flex w-full min-w-0 flex-col gap-3 bg-slate-50 p-3 text-left transition-colors hover:bg-primary/5 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary sm:flex-row sm:items-center sm:justify-between sm:p-4"
      >
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <span className="text-sm font-medium text-slate-700 shrink-0">
            Hiring Process
          </span>
          {instanceStatusBadge(instance.status as HiringProcessInstanceStatus)}
          {currentStage && (
            <span className="min-w-0 text-xs text-slate-600 [overflow-wrap:anywhere]">
              Stage: {currentStage.name}
            </span>
          )}
          <div className="w-36 max-w-full">
            <ProgressBar
              completed={completedRequired}
              total={requiredItems.length}
            />
          </div>
        </div>
        <div className="flex shrink-0 items-center justify-between gap-2 sm:justify-end">
          <span className="text-xs text-slate-500">
            {completedItems}/{totalItems} done
          </span>
          {isExpanded ? (
            <ChevronDown size={14} className="text-slate-400" />
          ) : (
            <ChevronRight size={14} className="text-slate-400" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div id={detailsId} className="min-w-0 border-t border-slate-200">
          {/* Stage stepper */}
          {templateStages.length > 1 && (
            <div className="space-y-3 border-b border-slate-200 bg-white p-3 sm:p-4">
              <div className="flex items-center gap-1 flex-wrap">
                {templateStages.map((stage, idx) => {
                  const isCurrentStage = stage.id === currentStageId;
                  const currentStageIdx = templateStages.findIndex(
                    (s) => s.id === currentStageId,
                  );
                  const isPastStage = currentStageIdx > idx;

                  return (
                    <React.Fragment key={stage.id}>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${
                          isCurrentStage
                            ? "bg-primary/10 text-primary"
                            : isPastStage
                              ? "bg-green-50 text-green-700"
                              : "bg-slate-100 text-slate-500"
                        }`}
                      >
                        {stage.name}
                      </span>
                      {idx < templateStages.length - 1 && (
                        <ArrowRight
                          size={10}
                          className="text-slate-300 shrink-0"
                        />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>

              {/* Move to stage dropdown */}
              {canEditOrgItems && (
                <div className="grid min-w-0 gap-2 sm:grid-cols-[auto_minmax(0,12rem)] sm:items-center">
                  <span className="text-xs text-slate-500">Move to stage:</span>
                  <Select
                    value={currentStageId ?? ""}
                    onValueChange={handleTransitionStage}
                    disabled={transitionStageMutation.isPending}
                  >
                    <SelectTrigger
                      aria-label="Move to stage"
                      className="w-full"
                    >
                      <SelectValue placeholder="Select stage..." />
                    </SelectTrigger>
                    <SelectContent>
                      {templateStages.map((stage) => (
                        <SelectItem
                          key={stage.id}
                          value={stage.id}
                          className="text-xs"
                        >
                          {stage.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Items grouped by stage (using hiringProcessStageId from items) */}
          {(() => {
            let globalIndex = 0;

            if (templateStages.length === 0) {
              // No template loaded yet or no stages — show flat list
              return (
                <div className="divide-y divide-slate-100">
                  {allItems.map((item) => {
                    const idx = globalIndex++;
                    return (
                      <HiringProcessInstanceItemRow
                        key={item.id}
                        item={item}
                        index={idx}
                        canEditOrgItems={canEditOrgItems}
                        onStatusChange={handleStatusChange}
                        isUpdating={updateStatusMutation.isPending}
                        candidateUserId={providerUserId}
                        jobPostId={jobPostId}
                        organizationBusinessUnitId={buId}
                      />
                    );
                  })}
                </div>
              );
            }

            return templateStages.map((stage) => {
              const stageItems = allItems.filter(
                (i) => i.hiringProcessStageId === stage.id,
              );
              const stageRequired = stageItems.filter((i) => i.isRequired);
              const stageDone = stageRequired.filter(
                (i) =>
                  i.status === HiringProcessItemStatus.Completed ||
                  i.status === HiringProcessItemStatus.Skipped ||
                  i.status === HiringProcessItemStatus.NotApplicable,
              ).length;
              const isCurrentStage = stage.id === currentStageId;

              return (
                <div key={stage.id}>
                  <div
                    className={`flex flex-wrap items-center justify-between gap-2 border-y border-slate-200 px-3 py-3 sm:px-4 ${
                      isCurrentStage ? "bg-primary/5" : "bg-slate-50"
                    }`}
                  >
                    <div className="flex min-w-0 flex-wrap items-center gap-2">
                      <Layers
                        size={12}
                        className={
                          isCurrentStage ? "text-primary" : "text-slate-400"
                        }
                      />
                      <span
                        className={`min-w-0 text-xs font-semibold uppercase tracking-wide [overflow-wrap:anywhere] ${
                          isCurrentStage ? "text-primary" : "text-slate-500"
                        }`}
                      >
                        {stage.name}
                      </span>
                      {isCurrentStage && (
                        <Badge className="bg-primary/10 text-primary border-primary/20 text-xs h-4 px-1.5">
                          Current
                        </Badge>
                      )}
                    </div>
                    <span className="text-xs text-slate-400">
                      {stageDone}/{stageRequired.length} done
                    </span>
                  </div>

                  {stageItems.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-400 italic bg-white">
                      No items in this stage.
                    </div>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {stageItems.map((item) => {
                        const idx = globalIndex++;
                        return (
                          <HiringProcessInstanceItemRow
                            key={item.id}
                            item={item}
                            index={idx}
                            canEditOrgItems={canEditOrgItems}
                            onStatusChange={handleStatusChange}
                            isUpdating={updateStatusMutation.isPending}
                            candidateUserId={providerUserId}
                            jobPostId={jobPostId}
                            organizationBusinessUnitId={buId}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            });
          })()}
        </div>
      )}
    </div>
  );
}
